import React, { useState } from 'react';
import Button from '@srm/ui/button';
import Hint from '@srm/ui/hint';
import Modal from '@srm/ui/modal';
import {
    IMatrixCell,
    IMatrixCellCommand,
    IMatrixParticipant,
    IMatrixPosition,
    IWinnerMatrix,
} from '../WinnerSelection2.types';
import { commentRequiredFor, complianceOf } from '../utils/dialogForm';
import { complianceAllowsSelection } from '../utils/matrixView';
import { DialogContext } from './DialogContext';
import { ApplyToSupplierOffers, ComplianceFields } from './DialogFields';

interface IProps {
    matrix: IWinnerMatrix;
    cell: IMatrixCell;
    position: IMatrixPosition;
    participant: IMatrixParticipant;
    busy: boolean;
    blocked?: boolean;
    error: string | undefined;
    /** Остальные предложения поставщика с правом ответа по ТЗ: ключ → ревизия. */
    otherOffers: Record<string, string>;
    onSave: (change: NonNullable<IMatrixCellCommand['compliance']>) => void;
    onClose: () => void;
}

/** Ответ и комментарий по ТЗ. Для лотового предложения ответ общий для всех позиций лота. */
const ComplianceDialog: React.FC<IProps> = ({
    matrix,
    cell,
    position,
    participant,
    busy,
    blocked,
    error,
    otherOffers,
    onSave,
    onClose,
}) => {
    const answers = matrix.compliance.answers;
    const [answer, setAnswer] = useState(cell.complianceAnswer ?? '');
    const [comment, setComment] = useState(cell.complianceComment ?? '');
    const [submitted, setSubmitted] = useState(false);
    const [applyToAll, setApplyToAll] = useState(false);
    const commentMissing = commentRequiredFor(answers, answer) && !comment.trim();
    const assigned = cell.result === 'main' || cell.result === 'reserve';
    const withdraws = assigned && !complianceAllowsSelection(complianceOf(answers, answer || null));

    const save = () => {
        setSubmitted(true);
        if (commentMissing) return;
        onSave({
            answer: answer || null,
            comment: comment.trim() || null,
            otherOfferRevisions: applyToAll ? otherOffers : undefined,
        });
    };

    const footer = (
        <div className="ws2-dialog__footer">
            <Button variant="outline-neutral" disabled={busy} onClick={onClose}>
                Отмена
            </Button>
            <Button variant="primary" onClick={save} loading={busy} disabled={busy || blocked}>
                Сохранить
            </Button>
        </div>
    );

    return (
        <Modal
            className="ws2-modal"
            width={640}
            size="m"
            header="Соответствие ТЗ"
            onClose={onClose}
            hasUnsavedInput={() =>
                applyToAll || answer !== (cell.complianceAnswer ?? '') || comment !== (cell.complianceComment ?? '')
            }
            closeOnEscape={!busy}
            closeDisabled={busy}
            footer={footer}
        >
            <div className="ws2-dialog">
                <DialogContext
                    supplier={participant.name}
                    subject={cell.lotOffer ? 'все позиции лота' : position.name}
                />
                {error && <Hint role="alert" title={error} tone="danger" maxWidth="100%" />}
                <ComplianceFields
                    answers={answers}
                    answer={answer}
                    comment={comment}
                    disabled={busy || blocked}
                    commentError={
                        submitted && commentMissing
                            ? 'Для частичного или отрицательного ответа нужен комментарий'
                            : undefined
                    }
                    onAnswer={setAnswer}
                    onComment={setComment}
                />
                <ApplyToSupplierOffers
                    count={Object.keys(otherOffers).length}
                    checked={applyToAll}
                    disabled={busy || blocked}
                    onChange={setApplyToAll}
                />
                {withdraws && (
                    <Hint
                        role="alert"
                        title="Этот ответ не допускает выбор: назначение будет снято, выбранное количество сохранится"
                        tone="warning"
                        maxWidth="100%"
                    />
                )}
            </div>
        </Modal>
    );
};

export default ComplianceDialog;
